import { cn } from '@/lib/utils';
import { Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ChatActionButton, parseChatActions } from './ChatActionButton';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  showActions?: boolean;
  assistantName?: string;
}

export function ChatMessage({ role, content, isStreaming, showActions, assistantName }: ChatMessageProps) {
  const isAssistant = role === 'assistant';
  const { text, actions } = isAssistant ? parseChatActions(content) : { text: content, actions: [] };

  return (
    <div
      className={cn(
        'flex gap-3 p-3 rounded-lg',
        isAssistant ? 'bg-muted/50' : 'bg-primary/5'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isAssistant ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
        )}
      >
        {isAssistant ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-muted-foreground mb-1">
          {isAssistant ? (assistantName || 'Assistant') : 'You'}
        </div>
        
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {text ? (
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="my-2 list-disc pl-4">{children}</ul>,
                ol: ({ children }) => <ol className="my-2 list-decimal pl-4">{children}</ol>,
                li: ({ children }) => <li className="mb-1">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          ) : isStreaming ? (
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : null}
        </div>

        {isAssistant && actions.length > 0 && !isStreaming && (
          <div className="mt-2 space-y-2">
            {actions.map((a, i) => (
              <ChatActionButton key={i} action={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
